import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { classNames } from "@/lib/utils";
import {
  FileEnvironment,
  capitalizedFileType,
  refetchVersionsForFile,
  useFileEnvironments,
} from "@/services/files.service";
import { Environment } from "@/types/app/environment";
import { File } from "@/types/app/file";
import AppDialog, { DialogSection } from "@components/library/AppDialog";
import Button from "@components/library/Button";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { CheckIcon } from "@heroicons/react/solid";
import { Checkbox } from "antd";
import { useMemo, useState } from "react";
import DefaultTag from "../Organizations/DefaultTag";
import { None } from "../Tables/Logs/LogCells";
import { ConfirmDeploymentDialogPage } from "./ConfirmDeploymentDialogPage";

interface DeploymentDialogProps {
  version: File | null;
  open: boolean;
  onClose: () => void;
  onDeploy?: () => void;
}

/** Dialog to deploy a specific version. */
export const ActiveDeploymentDeployDialog = ({ version = null, open, onClose, onDeploy }: DeploymentDialogProps) => {
  const { file, mutate: mutateFile } = useMonitoringFile();
  const [confirmAction, setConfirmAction] = useState<boolean>(false);

  const [selectedEnvironments, setSelectedEnvironments] = useState<Environment[]>([]);
  const { fileEnvironments, mutate: mutateFileEnvironments } = useFileEnvironments(file?.id);

  const environmentsAlreadyDeployedTo = useMemo(
    () => fileEnvironments?.filter((env) => env.file !== null && env.file.version_id === version?.version_id),
    [fileEnvironments, version],
  );

  // Filter out the Environments that are already selected
  const newEnvironments = selectedEnvironments.filter(
    (selectedEnv) => !environmentsAlreadyDeployedTo?.some((deployedEnv) => deployedEnv.id === selectedEnv.id),
  );

  if (!file) {
    return null;
  }

  if (version === null || fileEnvironments === undefined) {
    return null;
  }

  const handleClose = () => {
    setConfirmAction(false);
    setSelectedEnvironments([]);
    onClose();
  };

  const dialogTitle = (
    <>
      <div className="px-8 pt-8 text-18-24 font-bold text-gray-800 [text-wrap:balance]">
        Deploy {capitalizedFileType(file.type)}{" "}
        {version ? (
          <>
            {/* Absolute hack just to make this bigger without faffing with the component */}
            <div className="ml-28 inline-flex scale-[1.2]">
              <FileLabelWithPopover
                fileId={file.id}
                versionId={version.version_id}
                size={24}
                displayFile={false}
                tooltipContentProps={{
                  align: "start",
                  side: "bottom",
                }}
              />
            </div>
          </>
        ) : null}
      </div>
    </>
  );

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      width="medium"
      title={<div className="w-full justify-center">{dialogTitle}</div>}
      description={
        !confirmAction ? (
          <div className="flex flex-col px-8 text-14-20">
            <p>This will set the selected environments to use this {capitalizedFileType(file.type)} version.</p>
          </div>
        ) : null
      }
    >
      {!confirmAction ? (
        <SelectEnvironments
          versionId={version.version_id}
          fileEnvironments={fileEnvironments}
          newEnvironments={newEnvironments}
          environmentsSelected={selectedEnvironments}
          setEnvironmentsSelected={setSelectedEnvironments}
          setConfirmAction={setConfirmAction}
          onClose={handleClose}
        />
      ) : (
        <ConfirmDeploymentDialogPage
          fileId={file.id}
          fileEnvironments={fileEnvironments}
          display={"environments"}
          selectedVersion={version}
          onClose={handleClose}
          environmentsSelected={newEnvironments}
          backAction={() => {
            setConfirmAction(false);
          }}
          onDeploy={() => {
            onDeploy && onDeploy();
            setSelectedEnvironments([]);
            setConfirmAction(false);
            mutateFile();
            mutateFileEnvironments();
            refetchVersionsForFile(file.id);
          }}
        />
      )}
    </AppDialog>
  );
};

const getJoinedEnvironmentNames = (environments: Environment[]) => {
  const environmentContentLabel = environments.length > 1 ? "environments" : "environment";
  let joinedEnvironmentNames = "default";
  if (environments.length === 1) {
    joinedEnvironmentNames = environments[0].name;
  } else if (environments.length === 2) {
    joinedEnvironmentNames = `${environments[0].name} & ${environments[1].name}`;
  } else if (environments.length > 2) {
    const joinedNames = environments
      .slice(0, environments.length - 1)
      .map((environment) => environment.name)
      .join(", ");
    joinedEnvironmentNames = `${joinedNames} & ${environments[environments.length - 1].name}`;
  }
  return `${joinedEnvironmentNames} ${environmentContentLabel}`;
};

interface SelectEnvironmentsProps {
  versionId: string;
  fileEnvironments: FileEnvironment[];
  environmentsSelected: Environment[];
  newEnvironments: Environment[];
  setEnvironmentsSelected: (environments: Environment[]) => void;
  setConfirmAction: (confirmAction: boolean) => void;
  onClose: () => void;
}

/* A scrollable list of environments that the user can select to deploy to for a specific version. */
export const SelectEnvironments = ({
  versionId,
  fileEnvironments,
  environmentsSelected,
  setEnvironmentsSelected,
  newEnvironments,
  setConfirmAction,
  onClose,
}: SelectEnvironmentsProps) => {
  const handleCheckBoxChange = (environmentsSelected: Environment[], environment: Environment) => {
    // If the environment is already selected, remove it from the list. Otherwise, add it.
    const newEnvironmentsSelected = environmentsSelected.includes(environment)
      ? environmentsSelected.filter((selectedEnvironment) => selectedEnvironment.id !== environment.id)
      : [...environmentsSelected, environment];

    setEnvironmentsSelected(newEnvironmentsSelected);
  };

  const handleSelectAllCheckBoxChange = () => {
    setEnvironmentsSelected(environmentsSelected.length === fileEnvironments.length ? [] : fileEnvironments);
  };

  return (
    <div className="-my-8 flex flex-col justify-between gap-40">
      <div className="px-[24px]">
        <DialogSection>
          <div className="-mx-16 flex max-h-[270px] flex-col overflow-y-auto rounded border border-oldgray-400 text-13-16">
            <div className="flex h-48 w-full shrink-0 items-center border-b border-oldgray-300">
              <div className="flex w-1/2 min-w-0 items-center gap-6">
                <div className="flex select-none items-baseline justify-start gap-6 px-16 font-bold text-oldgray-900">
                  <Checkbox
                    tabIndex={-1}
                    indeterminate={
                      environmentsSelected.length > 0 && environmentsSelected.length < fileEnvironments.length
                    }
                    name="selectAll"
                    checked={environmentsSelected.length === fileEnvironments.length}
                    onClick={(event) => {
                      handleSelectAllCheckBoxChange();
                      event.stopPropagation();
                    }}
                  >
                    Environment
                  </Checkbox>
                </div>
              </div>
              <div className=" w-1/2 items-center justify-start px-16 pt-4 font-bold text-oldgray-900">
                Current deployment
              </div>
            </div>

            {fileEnvironments.map((environment) => {
              const alreadyDeployedHere = Boolean(environment.file?.version_id === versionId);
              return (
                <div
                  key={environment.id}
                  className={classNames(
                    "flex border-b border-oldgray-300",
                    alreadyDeployedHere ? "bg-diagonal-lines" : "",
                  )}
                >
                  <div
                    className={classNames(
                      "flew-row flex h-48 w-1/2 cursor-pointer items-center justify-start gap-6  px-16",
                    )}
                    onClick={() =>
                      alreadyDeployedHere ? null : handleCheckBoxChange(environmentsSelected, environment)
                    }
                  >
                    <Checkbox
                      disabled={alreadyDeployedHere}
                      className="mb-2 mr-2"
                      name="environment"
                      value={environment.id}
                      checked={alreadyDeployedHere || environmentsSelected.includes(environment)}
                    />
                    <div className="flex min-w-0  items-center gap-6">
                      <div className="truncate">{environment.name}</div>{" "}
                      {environment.tag == "default" && <DefaultTag />}
                    </div>
                  </div>

                  <div className="w-1/2">
                    <VersionNameCell key={environment.id} file={environment.file} active={alreadyDeployedHere} />
                  </div>
                </div>
              );
            })}
          </div>
        </DialogSection>
      </div>

      <div className="flex flex-row justify-between px-8 pb-8 align-middle">
        <Button
          size={36}
          onClick={async () => {
            onClose();
          }}
        >
          Cancel
        </Button>

        <Button
          size={36}
          styling="solid"
          shade={"black"}
          onClick={() => setConfirmAction(true)}
          IconLeft={CheckIcon}
          disabled={environmentsSelected.length === 0 || newEnvironments.length === 0}
        >
          Deploy
        </Button>
      </div>
    </div>
  );
};

export function VersionNameCell({ file, active }: { file: File | null; active: boolean }) {
  return (
    <div
      className={classNames(
        "flew-row flex h-48 w-full items-center justify-start gap-6 px-16",
        active ? " bg-diagonal-lines" : "",
      )}
    >
      {file ? (
        <div>
          <FileLabelWithPopover
            tooltipContentProps={{
              align: "end",
              side: "right",
            }}
            file={file}
            size={24}
            displayFile={false}
          />
        </div>
      ) : (
        <None />
      )}
    </div>
  );
}
